/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  DatePicker,
  Typography,
  Space,
  Divider,
} from "antd";
import { useTranslation } from "react-i18next";
import { apiClient } from "../Api/AllApi.js";
import {
  CalendarOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  AppstoreOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "../Styles/Dashboard.css";

const { Title } = Typography;

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const [analyticsData, setAnalyticsData] = useState({
    total_customers: 0,
    total_products: 0,
    total_orders: 0,
    storage_usage: 0,
  });
  const [totalStorage, setTotalStorage] = useState("0 Bytes");
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [categorySummary, setCategorySummary] = useState([]);
  const [stockSummary, setStockSummary] = useState([]);

  function parseLocalizedName(value, fallback = "") {
    try {
      if (typeof value === "string" && value.trim().startsWith("{")) {
        const parsed = JSON.parse(value);
        if (typeof parsed === "object") {
          return (
            parsed[i18n.language] ||
            parsed.en ||
            Object.values(parsed).find(Boolean) ||
            fallback
          );
        }
      }
      return value || fallback;
    } catch (error) {
      console.error("Error parsing localized name:", value, error);
      return fallback;
    }
  }

  useEffect(() => {
    const today = dayjs();
    setDateRange([today, today]);
    getAnalyticsData();
    getRecentOrders(today.format("YYYY-MM-DD"), today.format("YYYY-MM-DD"));
  }, [i18n.language]);

  const getAnalyticsData = async () => {
    try {
      const data = await apiClient.get("/dashboard/summary");
      setAnalyticsData(data.data);
      setTotalStorage(formatStorageSize(data.data.storage_usage || 0));
    } catch (error) {
      console.error("Error fetching analytics data:", error);
    }
  };

  const getRecentOrders = async (from, to, page = 1) => {
    setLoading(true);
    try {
      let url = `/dashboard/top-orders?limit=10&page=${page}`;
      if (from && to) {
        url += `&from=${from}&to=${to}`;
      }
      const response = await apiClient.get(url);
      setRecentOrders(
        (response.data.data || []).map((order) => ({
          id: order.id,
          customer: order.customer?.name || "-",
          date: order.created_at
            ? dayjs(order.created_at).format("DD/MM/YYYY")
            : "-",
          total: order.total_price,
        }))
      );
      setPagination({
        current: response.data.current_page,
        pageSize: Number(response.data.per_page),
        total: response.data.total,
      });
    } catch (error) {
      setRecentOrders([]);
    }
    setLoading(false);
  };

  const formatStorageSize = (sizeInBytes) => {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (!sizeInBytes) return "0 Bytes";
    const i = Math.floor(Math.log(sizeInBytes) / Math.log(1024));
    return (
      parseFloat((sizeInBytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const handleDateChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
      getRecentOrders(
        dates[0].format("YYYY-MM-DD"),
        dates[1].format("YYYY-MM-DD")
      );
    } else {
      setDateRange([null, null]);
      setRecentOrders([]); // Clear recent orders when no date range is selected
    }
  };

  const handleTableChange = (pagination) => {
    getRecentOrders(
      dateRange[0]?.format("YYYY-MM-DD"),
      dateRange[1]?.format("YYYY-MM-DD"),
      pagination.current
    );
  };

  const orderColumns = [
    {
      title: t("order"),
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: t("customer"),
      dataIndex: "customer",
      key: "customer",
    },
    {
      title: t("date"),
      dataIndex: "date",
      key: "date",
      width: 120,
    },
    {
      title: t("total"),
      dataIndex: "total",
      key: "total",
      render: (val) => (val ? Number(val).toLocaleString() + " IQD" : "-"),
      width: 120,
    },
    {
      title: t("actions"),
      key: "actions",
      width: 120,
      render: (_, record) => (
        <a
          href={`/TableOrder/InfoOrder/${record.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <button
            style={{
              background: "#1890ff",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "4px 12px",
              cursor: "pointer",
            }}
          >
            {t("View")}
          </button>
        </a>
      ),
    },
  ];

  useEffect(() => {
    apiClient
      .get("/dashboard/top-customers?limit=5")
      .then((res) => setTopCustomers(res.data))
      .catch(() => setTopCustomers([]));

    apiClient
      .get("/dashboard/top-products?limit=5")
      .then((res) => setTopProducts(res.data))
      .catch(() => setTopProducts([]));
  }, [i18n.language, dateRange]);

  useEffect(() => {
    const fetchCategorySummary = async () => {
      try {
        const response = await apiClient.get("/dashboard/category-summary", {
          params: {
            start_date: dateRange[0]?.format("YYYY-MM-DD"),
            end_date: dateRange[1]?.format("YYYY-MM-DD"),
          },
        });

        const summaryData = response.data.map((category) => {
          let categoryName;
          try {
            categoryName = parseLocalizedName(
              category.category_name,
              t("Unknown category")
            );
          } catch (error) {
            console.error(
              "Error parsing category name:",
              category.category_name,
              error
            );
            categoryName = category.category_name;
          }

          return {
            name: categoryName,
            total_quantity: category.total_quantity,
            total_weight: category.total_weight,
          };
        });

        const sortedSummaryData = summaryData.sort((a, b) => {
          if (a.name === "Uncategorized") return 1;
          if (b.name === "Uncategorized") return -1;
          return a.name.localeCompare(b.name);
        });

        setCategorySummary(sortedSummaryData);
      } catch (error) {
        console.error("Error fetching category summary:", error);
      }
    };

    if (dateRange[0] && dateRange[1]) {
      fetchCategorySummary();
    }
  }, [dateRange, i18n.language]);

  useEffect(() => {
    const fetchStockSummary = async () => {
      try {
        const response = await apiClient.get(
          "/dashboard/stock-category-summary"
        );
        const processedStockSummary = response.data.map((item) => {
          let categoryName;
          try {
            categoryName = parseLocalizedName(
              item.category_name,
              t("Unknown category")
            );
          } catch (error) {
            console.error(
              "Error parsing category name:",
              item.category_name,
              error
            );
            categoryName = item.category_name;
          }

          return {
            ...item,
            category_name: categoryName,
            total_stock_quantity: item.total_stock_quantity.toLocaleString(),
            total_stock_weight: item.total_stock_weight.toLocaleString(),
          };
        });

        setStockSummary(processedStockSummary);
      } catch (error) {
        console.error("Error fetching stock summary:", error);
      }
    };

    fetchStockSummary();
  }, []);

  const handleCategoryDateChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
    } else {
      setDateRange([null, null]); // Clear the date range without causing errors
    }
  };

  return (
    <div className="dashboard-page">
      <Title level={2} className="page-title" style={{ marginBottom: 24 }}>
        {t("dashboard")}
      </Title>

      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="stat-card stat-card--customers">
            <Statistic
              title={t("customers")}
              value={analyticsData.total_customers}
              prefix={<UserOutlined style={{ color: "#16a34a", fontSize: 28, marginLeft: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="stat-card stat-card--products">
            <Statistic
              title={t("products")}
              value={analyticsData.total_products}
              prefix={<AppstoreOutlined style={{ color: "#2563eb", fontSize: 28, marginLeft: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="stat-card stat-card--orders">
            <Statistic
              title={t("orders")}
              value={analyticsData.total_orders}
              prefix={<ShoppingCartOutlined style={{ color: "#d97706", fontSize: 28, marginLeft: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="stat-card stat-card--storage">
            <Statistic
              title={t("storage_usage")}
              value={totalStorage}
              prefix={<DatabaseOutlined style={{ color: "#7c3aed", fontSize: 28, marginLeft: 8 }} />}
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ background: '#e6f7ff' }}>
            <Statistic title={t('Total Revenue')} value={'-'} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ background: '#f6ffed' }}>
            <Statistic title={t('Total Paid')} value={'-'} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ background: '#fff1f0' }}>
            <Statistic title={t('Total Debt')} value={'-'} valueStyle={{ color: '#fa541c' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ background: '#fffbe6' }}>
            <Statistic title={t('Total Orders')} value={analyticsData.total_orders} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
      </Row> */}

      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Card title={t("Top Customers")} bordered={false}>
            <Table
              columns={[
                {
                  title: t("Customer"),
                  dataIndex: "customer",
                  key: "customer",
                },
                { title: t("Orders"), dataIndex: "orders", key: "orders" },
                {
                  title: t("Total"),
                  dataIndex: "total_price",
                  key: "total_price",
                  render: (v) => (v ? v.toLocaleString() + " IQD" : "-"),
                },
              ]}
              dataSource={topCustomers.map((c, i) => ({ key: i + 1, ...c }))}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title={t("Top Selling Products")} bordered={false}>
            <Table
              columns={[
                {
                  title: t("Product"),
                  dataIndex: "product",
                  key: "product",
                  render: (val) => {
                    try {
                      if (val) {
                        let nameObj;
                        if (
                          typeof val === "string" &&
                          val.trim().startsWith("{")
                        ) {
                          nameObj = JSON.parse(val);
                        }
                        if (nameObj && typeof nameObj === "object") {
                          return (
                            nameObj[i18n.language] ||
                            nameObj.en ||
                            Object.values(nameObj).find(Boolean) ||
                            t("No product available")
                          );
                        }
                        return val;
                      }
                    } catch (e) {
                      return val || t("No product available");
                    }
                    return t("No product available");
                  },
                },
                { title: t("Qty"), dataIndex: "qty", key: "qty" },
                {
                  title: t("Total"),
                  dataIndex: "revenue",
                  key: "revenue",
                  render: (v) => (v ? v.toLocaleString() + " IQD" : "-"),
                },
              ]}
              dataSource={topProducts.map((p, i) => ({ key: i + 1, ...p }))}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </Col>
      </Row>

      <Row
        gutter={[24, 24]}
        align="middle"
        style={{ marginBottom: 16, direction: "rtl" }}
      >
        <Col xs={24} sm={12} md={8}>
          <Space direction="horizontal" style={{ width: "100%" }}>
            <CalendarOutlined />
            <span style={{ fontWeight: 500 }}>{t("filter by date")}</span>
            <div style={{ direction: "ltr", width: "100%" }}>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={handleDateChange}
                format="YYYY-MM-DD"
                style={{ minWidth: 260 }}
                allowClear
                placeholder={[t("start Date"), t("end Date")]}
                inputReadOnly
              />
            </div>
          </Space>
        </Col>
      </Row>

      <Card
        title={t("Recent Orders")}
        style={{ marginBottom: 24 }}
        bordered={false}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={orderColumns}
          dataSource={recentOrders}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onChange={handleTableChange}
          style={{ minHeight: 200 }}
        />
      </Card>
      <Row
        gutter={[24, 24]}
        align="middle"
        style={{ marginBottom: 16, direction: "rtl" }}
      >
        <Col xs={24} sm={12} md={8}>
          <Space direction="horizontal" style={{ width: "100%" }}>
            <CalendarOutlined />
            <span style={{ fontWeight: 500 }}>
              {t("filter by date for category summary")}
            </span>
            <div style={{ direction: "ltr", width: "100%" }}>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={handleCategoryDateChange}
                format="YYYY-MM-DD"
                style={{ minWidth: 260 }}
                allowClear
                placeholder={[t("start Date"), t("end Date")]}
                inputReadOnly
              />
            </div>
          </Space>
        </Col>
      </Row>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Card title={t("Category Summary")} bordered={false}>
            <Table
              columns={[
                { title: t("Category"), dataIndex: "name", key: "name" },
                {
                  title: t("Total Quantity Sold"),
                  dataIndex: "total_quantity",
                  key: "total_quantity",
                  render: (value) => value.toLocaleString() || 0,
                },
                {
                  title: t("Total Weight Sold (kg)"),
                  dataIndex: "total_weight",
                  key: "total_weight",
                  render: (value) => value.toLocaleString() || 0,
                },
              ]}
              dataSource={categorySummary.map((item, index) => ({
                key: index,
                ...item,
              }))}
              pagination={false}
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title={t("Stock Summary")} bordered={false}>
            <Table
              columns={[
                {
                  title: t("Category"),
                  dataIndex: "category_name",
                  key: "category_name",
                },
                {
                  title: t("Total Stock Quantity"),
                  dataIndex: "total_stock_quantity",
                  key: "total_stock_quantity",
                  render: (value) => value.toLocaleString() || 0,
                },
                {
                  title: t("Total Stock Weight (kg)"),
                  dataIndex: "total_stock_weight",
                  key: "total_stock_weight",
                  render: (value) => value.toLocaleString() || 0,
                },
              ]}
              dataSource={stockSummary.map((item, index) => ({
                key: index,
                ...item,
              }))}
              pagination={false}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
